import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { ModelUse, PromptType } from '../../../types/backend';
import type { ModelUseType, PromptTypeType } from '../../../types/backend';
import { usePromptPreview } from '../hooks/usePromptPreview';
import type { PreviewPlaceholder } from '../hooks/usePromptPreview';

const JSON_PLACEHOLDERS = ['event', 'enrichment'];

// An agent prompt runs on its agent's model (AgentSpec) in production. The preview
// runs it on the pipeline use the tester names, so its result is a wording check.
const AGENT_PROMPT_TYPES: ReadonlySet<PromptTypeType> = new Set<PromptTypeType>([
  PromptType.SYSTEM_PROMPT,
  PromptType.AUTOMATION_PROMPT,
  PromptType.SKILL,
  PromptType.LEARNED_SKILL,
  PromptType.REPORT,
]);

// The uses a prose prompt can be tested for; enrichment's use is fixed by its type.
// quest_master is absent because its prompt is built in code, not managed in Prompt Manager.
const CHOOSABLE_USES: { value: ModelUseType; label: string }[] = [
  { value: ModelUse.EVENT_FORMATTING, label: 'Event formatting' },
  { value: ModelUse.MAPPING_SUGGESTION, label: 'Mapping suggestion' },
];

const helperFor = (p: PreviewPlaceholder, fromContext: boolean): string | undefined => {
  if (fromContext) return 'Filled from the customer in context. Type over it to test another.';
  return p.agent ? 'Agent variable, filled by the agent runtime in production' : undefined;
};

interface PromptPreviewPanelProps {
  content: string;
  customerId: string | undefined;
  promptType?: PromptTypeType;
}

const PromptPreviewPanel: React.FC<PromptPreviewPanelProps> = ({ content, customerId, promptType }) => {
  const [chosenUse, setChosenUse] = useState<ModelUseType | ''>('');
  const isAgentPrompt = promptType !== undefined && AGENT_PROMPT_TYPES.has(promptType);
  const isEnrichment = promptType === PromptType.ENRICHMENT;
  const needsChoice = !isEnrichment;
  const modelUse: ModelUseType | undefined = isEnrichment
    ? ModelUse.EVENT_ENRICHMENT
    : chosenUse !== ''
      ? chosenUse
      : undefined;
  const preview = usePromptPreview(content, customerId, promptType, modelUse);
  const canRun = modelUse !== undefined;

  return (
    <Box sx={{ px: 3, pt: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {preview.error && (
        <InlineAlert severity="error" onClose={() => preview.setError(null)}>
          {preview.error}
        </InlineAlert>
      )}

      {preview.placeholders.length > 0 ? (
        preview.placeholders.map((p) => {
          const isJson = !p.agent && JSON_PLACEHOLDERS.includes(p.name);
          return (
            <TextField
              key={p.token}
              label={p.token}
              value={preview.valueFor(p.token)}
              onChange={(e) => preview.setValue(p.token, e.target.value)}
              multiline={isJson}
              minRows={isJson ? 6 : undefined}
              size="small"
              fullWidth
              placeholder={isJson ? '{ "paste": "a sample payload" }' : `Value for ${p.token}`}
              helperText={helperFor(p, preview.isFromContext(p.token))}
              sx={isJson ? { '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8125rem' } } : undefined}
            />
          );
        })
      ) : (
        <Typography variant="body2" color="text.secondary">
          No placeholder variables detected — the prompt runs as written.
        </Typography>
      )}

      {needsChoice && (
        <TextField
          select
          label="Run on the model for"
          value={chosenUse}
          onChange={(e) => setChosenUse(e.target.value as ModelUseType | '')}
          size="small"
          fullWidth
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          helperText="The test runs on the model bound to this pipeline use."
        >
          <option value="" disabled>
            Choose a use
          </option>
          {CHOOSABLE_USES.map((use) => (
            <option key={use.value} value={use.value}>
              {use.label}
            </option>
          ))}
        </TextField>
      )}

      {isAgentPrompt && (
        <InlineAlert severity="info">
          A wording check: this runs the prompt once on a pipeline model, without the agent's
          model, tools or memory. Test the agent itself with a trial in Prompt Studio.
        </InlineAlert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={preview.running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
          onClick={preview.run}
          disabled={preview.running || !canRun}
        >
          {preview.running ? 'Running...' : 'Run'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Direct model run — agent tools and investigation context are not simulated; no rules or tickets are touched.
        </Typography>
      </Box>

      {preview.warnings.map((warning) => (
        <InlineAlert key={warning} severity="warning">
          {warning}
        </InlineAlert>
      ))}

      {preview.contractErrors.length > 0 && (
        <InlineAlert severity="error">
          The event pipeline would reject this output: {preview.contractErrors.join('; ')}
        </InlineAlert>
      )}

      {preview.contractChecked !== null && preview.contractErrors.length === 0 && (
        <InlineAlert severity="success">
          Output satisfies the {preview.contractChecked} contract the pipeline enforces.
        </InlineAlert>
      )}

      {preview.output !== null && (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            p: 2,
            maxHeight: 360,
            overflow: 'auto',
          }}
        >
          <Typography
            component="pre"
            variant="body2"
            sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.8125rem' }}
          >
            {preview.output}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PromptPreviewPanel;
